import React, { useMemo } from "react";
import { AspectRatio, Flex, Text, type SpaceProps } from "@chakra-ui/react";
import Image from "next/image";
import { FadeUpwards } from "ui/animations";
import { Button } from "ui/button";
import { AuraCard } from "ui/cards/aura-card";
import { TextLarge } from "ui/text";
import { Section } from "components/section";
import { type QuoteProps } from "./types";

type AuraCardQuoteProps = {
  /**
   * Disable the fade-in entrance animation for the text.
   */
  disableAnimation?: boolean; // eslint-disable-line react/boolean-prop-naming
  quote: QuoteProps;
} & SpaceProps;

/**
 * A quote displayed in large text on a section-width card with aura background.
 * Includes a subtle upward fade-in entrance animation for the text.
 */
export function AuraCardQuote({ disableAnimation, quote, ...spaceProps }: AuraCardQuoteProps) {
  const quoteNode = useMemo(() => {
    if (disableAnimation) {
      return <LargeQuote {...quote} />;
    }

    return (
      <FadeUpwards viewportAmount={0.4}>
        <LargeQuote {...quote} />
      </FadeUpwards>
    );
  }, [disableAnimation, quote]);

  return (
    <Section {...spaceProps}>
      <AuraCard>{quoteNode}</AuraCard>
    </Section>
  );
}

// This can be moved into ui/text if it becomes a visual pattern for quotes that gets reused.
// For now, it's still just a one-off quote style used with this component.
function LargeQuote({ quoteText, author, company, cta }: QuoteProps) {
  return (
    <Flex align="start" flexDir="column" gap="big">
      {company?.logo && (
        <AspectRatio ratio={company.logoRatio ?? 4 / 1} width={{ base: "100px", sm: "140px" }} pos="relative">
          <Image
            fill
            sizes="100%"
            alt={`${company.name} logo.`}
            src={`${company.logo}?w=600`}
            style={{ objectFit: "contain", objectPosition: "left" }}
          />
        </AspectRatio>
      )}

      <TextLarge
        size="lg"
        text={
          <Text as="span" fontWeight="normal">
            “{quoteText}”
          </Text>
        }
      />

      <Text textStyle="headline3" fontSize={{ base: "brand.md", md: "brand.lg" }}>
        {author.name},{" "}
        <Text as="span" fontWeight="normal">
          {author.job} {company && `at ${company.name}`}
        </Text>
      </Text>

      {cta && (
        <Button hasRightChevron href={cta.href}>
          {cta.text}
        </Button>
      )}
    </Flex>
  );
}
